import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  DeletionRequest,
  closeAccount,
  listDeletionRequests,
  purgeAccount,
} from '../../api/deletionRequests';
import { DELETION_REQUESTS_CHANGED } from '../DeletionRequestsNavLink/DeletionRequestsNavLink';
import './DeletionRequestsPage.css';

// Customers ask from the account page; the request has to be answered within
// a month (GDPR Art. 12(3)), so the due date leads every row. Two steps:
// close the login now, delete the data later (the backend waits an hour so a
// session opened before the close cannot write after the purge).

const STATUS_LABELS: Record<string, string> = {
  requested: 'Requested',
  closed: 'Account closed',
  purged: 'Data deleted',
};

const BLOCKER_LABELS: Record<string, string> = {
  with_lawyer: 'An application is still with the lawyer. Finish or close it first.',
};

const formatDate = (value: string | null | undefined): string => {
  if (!value) return 'n/a';
  const date = new Date(value);
  if (isNaN(date.getTime())) return 'n/a';
  return new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(date);
};

// The backend refuses a purge for an hour after the close (PURGE_AFTER).
export const PURGE_AFTER_MS = 60 * 60 * 1000;

export const purgeAvailableAt = (closedAt: string | null): Date | null => {
  if (!closedAt) return null;
  const closed = new Date(closedAt);
  return isNaN(closed.getTime()) ? null : new Date(closed.getTime() + PURGE_AFTER_MS);
};

const formatTime = (value: Date): string =>
  new Intl.DateTimeFormat('de-DE', { hour: '2-digit', minute: '2-digit' }).format(value);

export const daysLeft = (dueAt: string | null, now: Date = new Date()): number | null => {
  if (!dueAt) return null;
  const due = new Date(dueAt);
  if (isNaN(due.getTime())) return null;
  return Math.ceil((due.getTime() - now.getTime()) / 86_400_000);
};

const DueBadge: React.FC<{ request: DeletionRequest }> = ({ request }) => {
  if (request.status === 'purged') return null;
  const left = daysLeft(request.dueAt);
  if (left === null) return null;
  const urgent = left <= 7;
  return (
    <span className={`deletion-due${urgent ? ' deletion-due--urgent' : ''}`}>
      {left < 0 ? `${-left} days overdue` : `${left} days left`}
    </span>
  );
};

const RequestRow: React.FC<{
  request: DeletionRequest;
  busy: boolean;
  onClose: (request: DeletionRequest) => void;
  onPurge: (request: DeletionRequest) => void;
}> = ({ request, busy, onClose, onPurge }) => {
  const blocked = request.blockers.length > 0;
  const label = request.name || request.email || `Deleted account ${request.uid}`;
  const purgeAt = purgeAvailableAt(request.closedAt);
  const tooEarly = purgeAt !== null && purgeAt.getTime() > Date.now();
  return (
    <section className="deletion-request" aria-label={label}>
      <div className="deletion-request-header">
        <h3>{label}</h3>
        <span className={`deletion-status deletion-status--${request.status}`}>
          {STATUS_LABELS[request.status] || request.status}
        </span>
        <DueBadge request={request} />
      </div>
      <dl className="deletion-meta">
        {request.email && request.name && (
          <div><dt>Email</dt><dd>{request.email}</dd></div>
        )}
        <div><dt>Requested</dt><dd>{formatDate(request.requestedAt)}</dd></div>
        <div><dt>Due</dt><dd>{formatDate(request.dueAt)}</dd></div>
        {request.closedAt && (
          <div><dt>Closed</dt><dd>{formatDate(request.closedAt)} by {request.closedBy || 'n/a'}</dd></div>
        )}
        {request.purgedAt && (
          <div><dt>Deleted</dt><dd>{formatDate(request.purgedAt)} by {request.purgedBy || 'n/a'}</dd></div>
        )}
        {request.tenantId && (
          <div><dt>Partner</dt><dd>{request.tenantId}</dd></div>
        )}
      </dl>
      {request.cases.length > 0 && (
        <ul className="deletion-cases">
          {request.cases.map(item => (
            <li key={item.id}>
              <Link to={`/case/${item.id}`} className="service-link">{item.name || item.id}</Link>
              {' · '}{item.service || 'Application'}{' · '}{item.status || 'n/a'}
              {item.paid ? ' · paid (payment record kept)' : ''}
            </li>
          ))}
        </ul>
      )}
      {request.deleted && (
        <p className="deletion-deleted">
          Deleted: {Object.entries(request.deleted).map(([key, count]) => `${count} ${key}`).join(', ')}
        </p>
      )}
      {request.not_covered && request.not_covered.length > 0 && (
        <p className="deletion-not-covered">Check by hand: {request.not_covered.join(', ')}.</p>
      )}
      {blocked && request.status !== 'purged' && (
        <p className="deletion-blocked" role="note">
          {request.blockers.map(code => BLOCKER_LABELS[code] || code).join(' ')}
        </p>
      )}
      <div className="deletion-actions">
        {request.status === 'requested' && (
          <button type="button" className="download-button" disabled={busy || blocked} onClick={() => onClose(request)}>
            Close account
          </button>
        )}
        {request.status === 'closed' && (
          <>
            <button type="button" className="download-button deletion-purge" disabled={busy || blocked || tooEarly} onClick={() => onPurge(request)}>
              Delete data
            </button>
            {tooEarly && purgeAt && (
              <span className="deletion-hint">Possible from {formatTime(purgeAt)}, one hour after closing.</span>
            )}
          </>
        )}
      </div>
    </section>
  );
};

const DeletionRequestsPage: React.FC = () => {
  const { currentUser } = useAuth();
  const [requests, setRequests] = useState<DeletionRequest[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!currentUser) return;
    setLoading(true);
    setError('');
    try {
      const token = await currentUser.getIdToken();
      setRequests(await listDeletionRequests(token));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load deletion requests.');
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    load();
  }, [load]);

  const act = async (request: DeletionRequest, question: string, run: (token: string, uid: string) => Promise<DeletionRequest>) => {
    if (!currentUser || !window.confirm(question)) return;
    setBusy(request.uid);
    setError('');
    try {
      const token = await currentUser.getIdToken();
      const updated = await run(token, request.uid);
      setRequests(current => (current || []).map(item => (item.uid === updated.uid ? updated : item)));
      window.dispatchEvent(new Event(DELETION_REQUESTS_CHANGED));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The action failed.');
    } finally {
      setBusy(null);
    }
  };

  const label = (request: DeletionRequest) => request.email || request.name || request.uid;
  const open = (requests || []).filter(item => item.status !== 'purged').length;

  return (
    <div className="deletion-requests-page">
      <div className="task-view-header">
        <h2>Deletion requests</h2>
        <button type="button" className="download-button" onClick={load} disabled={loading}>
          Refresh
        </button>
      </div>
      {error && <div className="status-error-banner" role="alert">{error}</div>}
      {loading && !requests ? (
        <p>Loading deletion requests...</p>
      ) : requests ? (
        <>
          <p className="deletion-summary" data-testid="deletion-summary">
            <strong>{open}</strong> open. Close the account first: the customer is signed out and a new
            sign-in starts an empty account. Delete the data afterwards, before the due date.
          </p>
          {requests.length === 0 && <p className="deletion-empty">No deletion requests.</p>}
          {requests.map(request => (
            <RequestRow
              key={request.uid}
              request={request}
              busy={busy === request.uid}
              onClose={item => act(item, `Close the account of ${label(item)}? They are signed out and cannot sign in to it again.`, closeAccount)}
              onPurge={item => act(item, `Delete all data of ${label(item)}? This cannot be undone.`, purgeAccount)}
            />
          ))}
        </>
      ) : null}
    </div>
  );
};

export default DeletionRequestsPage;
